import React, {Component} from 'react';
import PropTypes from 'prop-types';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import InitialPageComponent from '../InitialPageComponent/InitialPageComponent';
import LoginPageComponent from '../LoginPageComponent/LoginPageComponent';
import SignUpPageComponent from '../SignUpPageComponent/SignUpPageComponent';
import SuccessPageComponent from '../SuccessPageComponent/SuccessPageComponent';
import ResetPasswordPageComponent from '../ResetPasswordPageComponent/ResetPasswordPageComponent';
import SocialLoginPageComponent from '../SocialLoginPageComponent/SocialLoginPageComponent';
import TermsAndConditionsPageComponent from '../TermsAndConditionsPageComponent/TermsAndConditionsPageComponent';
import authAccountTypes from '../../enums/AuthAccountTypes';
import './MainComponent.less';
import Modal from '@homeaway/react-modal';
import edapWrapper from '@homeaway/edap-wrapper';
import classNames from 'classnames';
import ClientLogger from '../../util/clientLogger';
import {apiPost} from '../../util/api.client';
import {loadLingua} from '../../util/dynamic.load';

ClientLogger.initialize({
    errorRoute: '/auth/ui/logs/errors',
    clientLogRoute: '/auth/ui/logs/cl'
});

const lingua = require('../../i18n/lingua.message');
const MessageReader = require('@homeaway/stab-message-reader');

class MainComponent extends Component {

    static get propTypes() {
        return {
            actionLocation: PropTypes.string,
            locale: PropTypes.string.isRequired,
            site: PropTypes.string.isRequired,
            siteName: PropTypes.string.isRequired,
            service: PropTypes.string.isRequired,
            handleCloseModal: PropTypes.func,
            handleLoginSuccess: PropTypes.func,
            useNewEndpoints: PropTypes.bool,
            // The flag to disable the social login when used in the multi-user-traveler app
            multiUserEnabled: PropTypes.bool,
            expediaIconDisplayed: PropTypes.bool
        };
    }

    static get defaultProps() {
        return {
            actionLocation: '-1',
            locale: 'en_US_HA',
            site: 'homeaway_us',
            siteName: 'HomeAway',
            service: 'misconfigured',
            modalMode: false,
            handleCloseModal: () => {},
            handleLoginSuccess: () => {},
            useNewEndpoints: false,
            multiUserEnabled: false,
            expediaIconDisplayed: false
        };
    }
    constructor(props) {
        super(props);

        this.state = {
            service: this.props.service,
            modalOpened: true,
            currentPage: 'initial',
            lingua,
            firstName: '',
            emailAddress: '',
            authAccountType: [],
            closeButton: true,
            passwordType: '',
            isWaiting: false,
            authJson: {},
            oauth: null,
            token: null,
            authStatus: null
        };

        // Bind to the correct context
        this.onClose = this.onClose.bind(this);
        this.onModalClose = this.onModalClose.bind(this);
        this.updatePage = this.updatePage.bind(this);
        this.setEmail = this.setEmail.bind(this);
        this.setFirstName = this.setFirstName.bind(this);
        this.performInitialBulkUpdate = this.performInitialBulkUpdate.bind(this);
        this.setAuthAccountType = this.setAuthAccountType.bind(this);
        this.clickBack = this.clickBack.bind(this);
        this.showHidePassword = this.showHidePassword.bind(this);
        this.getMainContent = this.getMainContent.bind(this);
        this.getClassName = this.getClassName.bind(this);
        this.enableLoading = this.enableLoading.bind(this);
        this.disableLoading = this.disableLoading.bind(this);
        this.performBulkPageUpdate = this.performBulkPageUpdate.bind(this);
        this.setAuthJson = this.setAuthJson.bind(this);
        this.loginSuccess = this.loginSuccess.bind(this);
        this.checkStatus = this.checkStatus.bind(this);
    }

    componentDidMount() {
        MessageReader.setLocale(this.props.locale);
        loadLingua(this.props.locale).then(() => {
            this.setState({linguaLoaded: true});
        }).catch((e) => {
            ClientLogger.logError(`Unable to load lingua keys for locale ${this.props.locale}. Error=${e}`);
            throw new Error(`Unable to load lingua keys for locale ${this.props.locale}. Error=${e}`);
        });

        apiPost('/auth/aam/v3/refreshlogin', {
            'tmDeviceIdKey': '00000000-0000-0000-0000-000000000000'
        }).then(this.checkStatus);

        if (this.props.modalMode) {
            edapWrapper().push({
                name: 'generic.modal',
                data: {modalaction: 'open', modalname: 'lex.auth', actionlocation: this.props.actionLocation}
            });
        }
    }

    onModalClose() {
        edapWrapper().push({
            name: 'generic.modal',
            data: {modalaction: 'close', modalname: 'lex.auth', actionlocation: this.props.actionLocation},
            options: {'delayProcessing': true}
        });
        this.props.handleCloseModal();
    }

    loginSuccess(json) {
        this.setAuthJson(json);
        edapWrapper().push({
            name: 'login.success',
            data: {loginsource: 'lex', logintype: 'ums', actionlocation: this.props.actionLocation},
            options: {'delayProcessing': true}
        });
        this.onClose();
    }

    checkStatus(response) {
        if (response.status === 200) {
            response.json().then((json) => {
                this.loginSuccess(json);
            }, (err) => {
                ClientLogger.logError(`error occured on recreate session error=${err}`);
            });
        }
    }

    enableLoading() {
        this.setState({isWaiting: true});
    }

    disableLoading() {
        this.setState({isWaiting: false});
    }

    setAuthJson(json) {
        this.setState({authJson: json});
    }

    onClose() {
        // User is authenticated if authJson is not an empty object
        // so, let the consuming  application know that there was a
        // successful login and send them the json response.
        if (Object.keys(this.state.authJson).length > 0) {
            this.props.handleLoginSuccess(this.state.authJson);
        }

        if (this.props.modalMode) {
            this.setState({modalOpened: false});
        }
    }

    performInitialBulkUpdate(pageName, emailAddress, status) {
        let currentList = this.state.authAccountType.slice();
        let newStatuses = status.slice();
        if (currentList.length > 0) {
            currentList.concat(newStatuses);
        } else {
            currentList = newStatuses;
        }
        if (this.state.service === 'misconfigured') { // cannot set href as default prop or tests fail so handling here, mostly for local testing
            this.setState({service: window.location.href});
        }
        this.setState({
            currentPage: pageName,
            closeButton: true,
            passwordType: 'password',
            emailAddress,
            authAccountType: currentList,
            isWaiting: false
        });
    }

    updatePage(pageName) {
        this.setState({
            currentPage: pageName,
            closeButton: true,
            passwordType: 'password'
        });
    }

    performBulkPageUpdate(pageName, emailAddress = null, oauth = null, token = null) {
        this.setState({
            currentPage: pageName,
            emailAddress,
            closeButton: true,
            oauth: [oauth],
            token,
            passwordType: 'password'
        });
    }

    setAuthAccountType(status) {
        let currentList = this.state.authAccountType.slice();
        currentList.push(status);
        this.setState({
            authAccountType: currentList
        });
    }

    setEmail(emailaddress) {
        this.setState({emailAddress: emailaddress});
    }

    setFirstName(firstname) {
        this.setState({firstName: firstname});
    }

    clickBack() {
        edapWrapper().push({
            name: 'generic.click',
            data: {eventcategory: 'lex.auth', eventaction: 'backButton.click', actionlocation: this.props.actionLocation},
            options: {'delayProcessing': true}
        });
        this.setState({
            currentPage: 'initial',
            closeButton: true
        });
    }

    showHidePassword(e) {
        e.preventDefault();
        e.stopPropagation();
        const shouldHide = this.state.passwordType === 'input';
        const eventaction = shouldHide ? 'hide' : 'show';
        edapWrapper().push({
            name: 'generic.click',
            data: {eventcategory: 'lex.auth', eventaction: `${eventaction}Pwd.click`, actionlocation: this.props.actionLocation},
            options: {'delayProcessing': true}
        });
        this.setState({
            passwordType: shouldHide ? 'password' : 'input'
        });
    }

    getContent(displayPage) {
        let content = '';
        switch (displayPage) {
            case authAccountTypes.UMS:
                content = (
                    <LoginPageComponent
                        actionLocation={this.props.actionLocation}
                        lingua={this.state.lingua}
                        siteName={this.props.siteName}
                        emailAddress={this.state.emailAddress}
                        service={this.state.service}
                        updatePage={this.updatePage}
                        authAccountType={this.state.authAccountType}
                        onClose={this.onClose}
                        passwordType = {this.state.passwordType}
                        showHidePassword={this.showHidePassword}
                        isWaiting={this.state.isWaiting}
                        enableLoading={this.enableLoading}
                        disableLoading={this.disableLoading}
                        setAuthJson={this.setAuthJson}
                        site={this.props.site}
                        useNewEndpoints={this.props.useNewEndpoints}
                        multiUserEnabled={this.props.multiUserEnabled}
                        expediaIconDisplayed={this.props.expediaIconDisplayed}

                    />
                );
                break;
            case authAccountTypes.GOOGLE:
                content = (
                    <SocialLoginPageComponent
                        actionLocation={this.props.actionLocation}
                        lingua={this.state.lingua}
                        firstName={this.state.firstName}
                        emailAddress={this.state.emailAddress}
                        service={this.state.service}
                        authAccountType={this.state.authAccountType}
                        onClose={this.onClose}
                        performBulkPageUpdate={this.performBulkPageUpdate}
                        setAuthJson={this.setAuthJson}
                        site={this.props.site}
                        useNewEndpoints={this.props.useNewEndpoints}
                        multiUserEnabled ={this.props.multiUserEnabled}
                        updatePage={this.updatePage}
                        expediaIconDisplayed={this.props.expediaIconDisplayed}
                    />
                );
                break;
            case authAccountTypes.FACEBOOK:
                content = (
                    <SocialLoginPageComponent
                        actionLocation={this.props.actionLocation}
                        lingua={this.state.lingua}
                        firstName={this.state.firstName}
                        emailAddress={this.state.emailAddress}
                        service={this.state.service}
                        authAccountType={this.state.authAccountType}
                        onClose={this.onClose}
                        setAuthJson={this.setAuthJson}
                        performBulkPageUpdate={this.performBulkPageUpdate}
                        site={this.props.site}
                        useNewEndpoints={this.props.useNewEndpoints}
                        multiUserEnabled ={this.props.multiUserEnabled}
                        updatePage={this.updatePage}
                        expediaIconDisplayed={this.props.expediaIconDisplayed}
                    />
                );
                break;
            case authAccountTypes.EXPEDIA:
                content = (
                    <SocialLoginPageComponent
                        lingua = {this.state.lingua}
                        firstName = {this.state.firstName}
                        emailAddress = {this.state.emailAddress}
                        service = {this.state.service}
                        authAccountType={this.state.authAccountType}
                        onClose={this.onClose}
                        setAuthJson={this.setAuthJson}
                        performBulkPageUpdate={this.performBulkPageUpdate}
                        site={this.props.site}
                        useNewEndpoints={this.props.useNewEndpoints}
                        multiUserEnabled ={this.props.multiUserEnabled}
                        updatePage={this.updatePage}
                        expediaIconDisplayed={this.props.expediaIconDisplayed}
                    />
                );
                break;
            case 'TermsAndConditions':
                content = (
                    <TermsAndConditionsPageComponent
                        emailAddress={this.state.emailAddress}
                        lingua={this.state.lingua}
                        site={this.props.site}
                        siteName={this.props.siteName}
                        isWaiting={this.state.isWaiting}
                        updatePage={this.updatePage}
                        performBulkPageUpdate={this.performBulkPageUpdate}
                    />
                );
                break;

            case authAccountTypes.SIGNUP:
                content = this.getSignUpContent();
                break;
            case 'success':
                content = (
                    <SuccessPageComponent
                        lingua={this.state.lingua}
                        emailAddress = {this.state.emailAddress}
                        firstName = {this.state.firstName}
                        onClose={this.onClose}
                    />
                );
                break;
            case 'passwordreset':
                content = (
                    <ResetPasswordPageComponent
                        lingua={this.state.lingua}
                        service = {this.state.service}
                        updatePage={this.updatePage}
                    />
                );
                break;
            case 'postTandC':
                if (this.state.oauth) {
                    content = (
                        <SocialLoginPageComponent
                            lingua = {this.state.lingua}
                            firstName = {this.state.firstName}
                            emailAddress = {this.state.emailAddress}
                            service = {this.state.service}
                            authAccountType={this.state.oauth}
                            onClose={this.onClose}
                            setAuthJson={this.setAuthJson}
                            performBulkPageUpdate={this.performBulkPageUpdate}
                            token={this.state.token}
                            site={this.props.site}
                            useNewEndpoints={this.props.useNewEndpoints}
                            multiUserEnabled ={this.props.multiUserEnabled}
                            updatePage={this.updatePage}
                            expediaIconDisplayed={this.props.expediaIconDisplayed}
                        />
                    );
                } else {
                    content = this.getSignUpContent();
                }
                break;
            default: // this would be 'initial' screen
                content = (
                    <InitialPageComponent
                        actionLocation={this.props.actionLocation}
                        lingua={this.state.lingua}
                        siteName={this.props.siteName}
                        firstName={this.state.firstName}
                        currentPage={this.state.currentPage}
                        performInitialBulkUpdate={this.performInitialBulkUpdate}
                        setAuthAccountType={this.setAuthAccountType}
                        authStatus={this.state.authStatus}
                        locale = {this.props.locale}
                        onClose={this.onClose}
                        isWaiting={this.state.isWaiting}
                        enableLoading={this.enableLoading}
                        disableLoading={this.disableLoading}
                        updatePage={this.updatePage}
                        site={this.props.site}
                        setAuthJson={this.setAuthJson}
                        performBulkPageUpdate={this.performBulkPageUpdate}
                        useNewEndpoints={this.props.useNewEndpoints}
                        multiUserEnabled={this.props.multiUserEnabled}
                        expediaIconDisplayed={this.props.expediaIconDisplayed}
                    />
                );
                break;
        }
        return content;
    }

    getSignUpContent() {
        return (
            <SignUpPageComponent
                actionLocation={this.props.actionLocation}
                lingua={this.state.lingua}
                site={this.props.site}
                emailAddress={this.state.emailAddress}
                service={this.state.service}
                updatePage={this.updatePage}
                setEmail={this.setEmail}
                setFirstName={this.setFirstName}
                authStatus="SIGNUP"
                passwordType={this.state.passwordType}
                showHidePassword={this.showHidePassword}
                isWaiting={this.state.isWaiting}
                enableLoading={this.enableLoading}
                disableLoading={this.disableLoading}
                performBulkPageUpdate={this.performBulkPageUpdate}
                setAuthJson={this.setAuthJson}
                useNewEndpoints={this.props.useNewEndpoints}
                multiUserEnabled={this.props.multiUserEnabled}
            />
        );
    }

    getMainContent() {
        const content = this.getContent(this.state.currentPage);
        if (this.props.modalMode) {
            return (<Modal
                id="lex-modal"
                isOpen={this.state.modalOpened}
                onClose= {this.onClose}
                onModalClose={this.onModalClose}
                isInstructional
                closeButton={this.state.closeButton}
                header
                    >
                {content}
            </Modal>);
        }
        return content;
    }

    getClassName() {
        const displayPage = this.state.currentPage;
        let modeClassName;
        // CSS will be white content only in these 2 cases, otherwise handle modal & regular page component css separately
        if (displayPage === 'success' || displayPage === 'passwordreset') {
            modeClassName = 'WhiteContent';
            modeClassName = this.props.modalMode ? `${modeClassName}` : `${modeClassName} ${modeClassName}--desktop`;
        } else {
            modeClassName = this.props.modalMode ? 'AuthModal' : 'RegularContent';
            // now we add brand
            modeClassName = (this.props.site === 'vrbo') ? `${modeClassName} ${modeClassName}--vrbo` : `${modeClassName} ${modeClassName}--gradient`;
        }

        return classNames(`${modeClassName}`);
    }

    render() {
        if (!this.state.linguaLoaded) {
            return null;
        }
        const modeClassName = this.getClassName();
        return (
            <div className={modeClassName} >
                <ErrorBoundary clientLogger={ClientLogger}>
                    {this.getMainContent()}
                </ErrorBoundary>
            </div>
        );
    }
}

export default MainComponent;
